import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { usePage } from '../../../../contexts/usePageContext';
import { RootState } from '../../../../redux/store';
import { findStructure, getTranslation } from '../../../../utils/dataTypes';
import { displayValueByType, getModelInfo, isWeightModelField } from '@/utils/structureUtils';
import { findNormalizedMatchRange, normalizeSearch } from '@/utils/stringUtils';
import { GroupedField, GroupedFieldsMap } from '../types';

type UseInfoListParams = {
 data: unknown;
 customFields?: string[] | null;
};

const getValueByPath = (source: Record<string, unknown>, path: string): unknown =>
 path.split('.').reduce<unknown>((acc, part) => {
  if (acc == null || typeof acc !== 'object') {
   return undefined;
  }
  return (acc as Record<string, unknown>)[part];
 }, source);

const getRenderedValueText = (item: GroupedField): string => {
 const rendered = displayValueByType(item.Value, item);
 if (
  typeof rendered === 'string' ||
  typeof rendered === 'number' ||
  typeof rendered === 'boolean'
 ) {
  return String(rendered);
 }
 if (
  typeof item.Value === 'string' ||
  typeof item.Value === 'number' ||
  typeof item.Value === 'boolean'
 ) {
  return String(item.Value);
 }
 return '';
};

const useInfoList = ({ data, customFields }: UseInfoListParams) => {
 const { pageName } = usePage();
 const { t } = useTranslation();
 const [searchValue, setSearchValue] = useState('');

 const structure = useSelector((state: RootState) => {
  if (pageName) {
   const pageState = state[pageName as keyof RootState];
   if (pageState && typeof pageState === 'object' && 'structure' in pageState) {
    return pageState.structure;
   }
  }
  return null;
 });

 const fieldsWithData = useMemo(() => {
  if (!data || typeof data !== 'object') {
   return [] as GroupedField[];
  }
  const dataObject = data as Record<string, unknown>;
  const fields = customFields ?? Object.keys(dataObject);
  if (!structure || !Array.isArray(fields)) {
   return [] as GroupedField[];
  }
  return fields
   .map((field) => {
    const resolvedValue = getValueByPath(dataObject, field);
    const structureField = findStructure(structure, field);
    if (!structureField) {
     return null;
    }

    let value = resolvedValue;
    let innerValue: string | number | null = null;

    if (structureField.Type === 'model' && resolvedValue) {
      if (isWeightModelField(structureField)) {
       if (resolvedValue && typeof resolvedValue === 'object') {
        const modelValue = resolvedValue as Record<string, unknown>;
        value = modelValue.Weight;
       }
      } else {
       const { id, name } = getModelInfo(
        structureField.ForeignKeyDefaultFilter || '',
       );
       if (resolvedValue && typeof resolvedValue === 'object') {
        const modelValue = resolvedValue as Record<string, unknown>;
        const nextInnerValue = modelValue[id];
        if (typeof nextInnerValue === 'string' || typeof nextInnerValue === 'number') {
         innerValue = nextInnerValue;
        }
        value = modelValue[name];
       }
      }
    }

    if (structureField.Type === 'array') {
     value = t('user.sidebar.infoList.noArraySupport');
    }
    if (structureField.Type === 'boolean') {
     value =
      typeof resolvedValue === 'boolean'
       ? resolvedValue
         ? t('global.yes')
         : t('global.no')
       : '-';
    } else if (resolvedValue === null || resolvedValue === undefined) {
     value = resolvedValue;
    }

    const nextField: GroupedField = {
     ...structureField,
     Key: field,
     Translation: getTranslation(structure, field),
     Value: value,
     InnerValue: innerValue,
     Group: structureField.Group ?? 'null',
     RenderedValue: displayValueByType(value, structureField),
     RenderedValueText: '',
    };

    return {
     ...nextField,
     RenderedValueText: getRenderedValueText(nextField),
    };
   })
   .filter((item): item is GroupedField => Boolean(item));
 }, [customFields, data, structure, t]);

 const groups = useMemo(() => {
  const nextGroups = fieldsWithData
   .filter((field) => field.Type !== 'array' && field.Priority)
   .reduce((accumulator, field) => {
    accumulator[field.Group] = accumulator[field.Group] || [];
    accumulator[field.Group].push(field);
    return accumulator;
   }, {} as GroupedFieldsMap);

  const sortedGroupKeys = Object.keys(nextGroups).sort((left, right) => {
   if (left === 'null') {
    return 1;
   }
   if (right === 'null') {
    return -1;
   }
   return left.localeCompare(right);
  });

  return sortedGroupKeys.reduce((accumulator, groupKey) => {
   accumulator[groupKey] = [...nextGroups[groupKey]].sort(
    (left, right) => (left.Priority ?? 0) - (right.Priority ?? 0),
   );
   return accumulator;
  }, {} as GroupedFieldsMap);
 }, [fieldsWithData]);

 const normalizedSearch = normalizeSearch(searchValue.trim());

 const filteredGroups = useMemo(() => {
  if (!normalizedSearch) {
   return groups;
  }

  return Object.entries(groups).reduce((accumulator, [groupKey, fields]) => {
   const filteredFields = fields.filter((field) => {
    const normalizedTranslation = normalizeSearch(field.Translation || '');
    const normalizedValue = normalizeSearch(field.RenderedValueText);
    return (
     normalizedTranslation.includes(normalizedSearch) ||
     normalizedValue.includes(normalizedSearch)
    );
   });

   if (filteredFields.length > 0) {
    accumulator[groupKey] = filteredFields;
   }
   return accumulator;
  }, {} as GroupedFieldsMap);
 }, [groups, normalizedSearch]);

 const groupKeys = useMemo(() => Object.keys(groups), [groups]);
 const filteredGroupKeys = useMemo(
  () => Object.keys(filteredGroups),
  [filteredGroups],
 );

 const getMatchRange = (text: string) =>
  findNormalizedMatchRange(text || '', normalizedSearch || '');

 return {
  filteredGroupKeys,
  filteredGroups,
  getMatchRange,
  groupKeys,
  normalizedSearch,
  searchValue,
  setSearchValue,
 };
};

export default useInfoList;
